import { type ExperienceActivationInstance } from 'sd-types/app/experience/activation'
import { type ExperienceActivationPayload } from '../../../../../types/models'
import { applyInlineStyles } from '../../../../../utils/dom-style'
import { logger } from '../../../../../utils/logger'
import { mergeArrays } from '../../../../../utils/merge-arrays'
import { type EmbedElementContext } from '../../../../element-embedder'
import { type EmbedIFrameComponent } from '../../../../embed-component'
import { type EmbedTarget } from '../../../../embed-targets/embed-target'
import { type MediaEventMap, type MediaControls } from '../../../../media'
import { BaseExperienceAdapter } from '../base'
import { type IExperienceAdapter } from '../experience-adapter'
import { type IAnalytics } from '../../analytics/analytics'

export class IFrameExperienceAdapter extends BaseExperienceAdapter implements IExperienceAdapter {
  constructor (
    public readonly target: EmbedTarget,
    public readonly iFrameComponent: EmbedIFrameComponent,
    public readonly getCurrentActivations: () => ExperienceActivationPayload[]
  ) {
    super()
  }

  analytics (): IAnalytics {
    return {
      sendAnalytics: (event: string, payload?: any) => {
        this.ctx.experienceProtocol.send('sendAnalytics', { event, payload })
      },
      sendActivationAnalytics: (event: string, activation: ExperienceActivationInstance, payload?: any) => {
        this.ctx.experienceProtocol.send('sendActivationAnalytics', { event, activation, payload })
      }
    }
  }

  get ctx (): EmbedElementContext {
    return this.target.ctx
  }

  get mediaControl (): MediaControls | undefined {
    return this.target.mediaControl
  }

  #setupConnection () {
    this.ctx.eventEmitter.on('playerjs.updateCurrentActivations', (items: ExperienceActivationPayload[]) => {
      // Merge items by id to avoid re-rendering
      const merged = mergeArrays<ExperienceActivationPayload>(this.getCurrentActivations(), items, (item) => item.activation?.instance?.id)
      this.onCurrentActivationsUpdateHandler(merged)
    })
    if (this.mediaControl != null && this.ctx.settings.overlayTimeSync === true) {
      const events: Array<keyof MediaEventMap> = ['timeupdate', 'seeked']
      events.forEach((ev) => {
        this.mediaControl?.on?.(ev, () => {
          (this.mediaControl != null) && this.ctx.experienceProtocol.send('setCurrentTime', this.mediaControl.currentTime)
        })
      })
    }
  }

  async initialize (): Promise<void> {
    const iframeStyles = {
      display: 'none',
      width: 1,
      height: 1
    }
    applyInlineStyles(iframeStyles, this.iFrameComponent.rootEl)
    this.#setupConnection()
    if (this.mediaControl != null) {
      this.setupAnalytics(this.mediaControl)
    }
    this.ctx.targetEl.after(this.iFrameComponent.rootEl)
    this.ctx.experienceProtocol.waitForReady()
      .then(async () => await this.ctx.experienceProtocol.call('getConfig'))
      .then(async (config) => {
        logger.debug('config', config)
        await this.onReadyHandler()
      })
      .catch(logger.error)
  }
}
